import { cloneDeep, get, isEqual } from 'lodash'

import { getString } from '@sigma-frontend-libs/commons'

import { cachedItems } from '../cache'

/**
 * Список элементов выпадающего списка: источник элементов, поля
 * значения, заголовка и блокировки, приведение элементов к единому
 * виду и фильтрация по строке поиска.
 */
export const items = {
  props: {
    /**
     * Элементы для выбора. Используются, пока элементы не
     * загружены из сервиса.
     *
     * @type {Array<object|string|number>}
     * @default []
     */
    items: {
      type: Array,
      default: () => {
        return []
      }
    },
    /**
     * Элементы, добавляемые в начало списка перед основными
     * элементами.
     *
     * @type {Array<object|string|number>}
     * @default []
     */
    itemsPrepend: {
      type: Array,
      default: () => {
        return []
      }
    },
    /**
     * Значения элементов, которые выводятся заблокированными.
     *
     * @type {Array<*>}
     * @default []
     */
    itemsDisabled: {
      type: Array,
      default: () => {
        return []
      }
    },

    /**
     * Имя поля элемента с заголовком. Заголовок выводится в списке
     * и в метке выбранного значения.
     *
     * @type {string}
     * @default 'title'
     */
    itemTitle: {
      type: String,
      default: 'title'
    },
    /**
     * Имя поля элемента со значением. Если не задано, поле
     * определяется вычисляемым свойством $_itemValue.
     *
     * @type {string}
     * @default undefined
     */
    itemValue: {
      type: String,
      default: undefined
    },
    /**
     * Имя поля элемента с признаком блокировки.
     *
     * @type {string}
     * @default 'disabled'
     */
    itemDisabled: {
      type: String,
      default: 'disabled'
    },

    /**
     * Проверка блокировки элемента. Если задана, полностью заменяет
     * проверку по полю itemDisabled и по списку itemsDisabled.
     *
     * @type {Function}
     * @default undefined
     */
    disabledItemCallback: {
      type: Function,
      default: undefined
    },

    /**
     * Имена дополнительных полей элемента, которые переносятся в
     * приведенный элемент помимо заголовка и значения.
     *
     * @type {Array<string>}
     * @default []
     */
    picks: {
      type: Array,
      default: () => {
        return []
      }
    }
  },

  /**
   * Локальное состояние списка элементов.
   * Поле _items хранит элементы, загруженные из сервиса; пока оно не
   * заполнено, используется свойство items.
   *
   * @returns {{ _items: Array<object>|undefined }} Локальное состояние.
   */
  data() {
    return {
      _items: undefined
    }
  },

  computed: {
    /**
     * Отдает имя поля элемента со значением.
     * Если задано свойство itemValue, отдается оно. Если задано
     * свойство service, отдается 'id'. Иначе отдается 'value'.
     *
     * @returns {string} Имя поля со значением.
     */
    $_itemValue() {
      if (this.itemValue) {
        return this.itemValue
      }
      if (this.service) {
        return 'id'
      }

      return 'value'
    },

    /**
     * Отдает полный список элементов, приведенных к единому виду.
     * Сначала идут элементы itemsPrepend, затем элементы, загруженные
     * из сервиса, а при их отсутствии - элементы свойства items.
     *
     * @returns {Array<object>} Приведенные элементы списка.
     */
    displayItems() {
      return [].concat(this.itemsPrepend, this._data._items || this.items).map(this._clearItem)
    },

    /**
     * Отдает элементы, прошедшие фильтр поиска, с отметкой подсветки.
     * Подсветка считается по позиции в этом же списке, потому что
     * рендерится именно он и по нему же двигается курсор клавиатуры.
     *
     * @returns {Array<object>} Отфильтрованные элементы списка.
     */
    filteredItems() {
      const result = this.displayItems.filter(({ _filtered }) => {
        return _filtered
      })

      return result.map((item, index) => {
        return {
          ...item,
          _hovered: this._data._cursorPosition === index
        }
      })
    }
  },

  watch: {
    /**
     * Отслеживает изменения списка элементов с глубоким сравнением.
     * Каждый элемент сохраняется в общем кеше по своему значению.
     * Если список не пуст, состав выбранных элементов пересобирается.
     */
    displayItems: {
      handler(value) {
        for (const item of value) {
          const key = getString(get(item, this.$_itemValue, item))
          cachedItems.set(key, item)
        }
        if (value.length) {
          this._fillSelectedItems()
        }
      },
      deep: true
    }
  },

  methods: {
    /**
     * Отдает заголовок элемента.
     * Поля перебираются в порядке itemTitle, 'title', 'name', и
     * отдается первое найденное значение. Если ни одно поле не
     * найдено, отдается undefined.
     *
     * @param {object|*} item Элемент списка.
     * @returns {string|undefined} Заголовок элемента.
     */
    _getTitleFromItem(item) {
      const fields = [ this.itemTitle, 'title', 'name' ]
      for (const field of fields) {
        const title = get(item, field)
        if (title !== undefined) {
          return title
        }
      }
    },

    /**
     * Отдает признак блокировки элемента.
     * Если задано свойство disabledItemCallback, отдается его
     * результат. Иначе отдается true, если элемент помечен полем
     * itemDisabled или его значение есть в списке itemsDisabled.
     *
     * @param {object|*} item Элемент списка.
     * @returns {boolean} Признак блокировки элемента.
     */
    _getDisabled(item) {
      if (this.disabledItemCallback) {
        return this.disabledItemCallback(item)
      }

      const disabled = get(item, this.itemDisabled, false)
      const itemDisabled = !!~this.itemsDisabled.findIndex(value => {
        return this._compareValues(value, item)
      })

      return disabled || itemDisabled
    },

    /**
     * Сравнивает два элемента по их значениям.
     * Значение берется из поля $_itemValue, а при его отсутствии - из
     * самого аргумента.
     *
     * @param {object|*} a Первый элемент или его значение.
     * @param {object|*} b Второй элемент или его значение.
     * @returns {boolean} Признак совпадения значений.
     */
    _compareValues(a, b) {
      const valueA = get(a, this.$_itemValue, a)
      const valueB = get(b, this.$_itemValue, b)

      return isEqual(valueA, valueB)
    },

    /**
     * Приводит элемент к единому виду с полями заголовка, значения
     * и служебными признаками.
     *
     * Если элемент уже приведен, он отдается без изменений. Поля из
     * списка picks переносятся в результат, кроме полей заголовка и
     * значения. Исходный элемент сохраняется в поле _item.
     *
     * Признак _selected выставляется, если значение есть среди
     * выбранных. Признак _filtered выставляется, если нет строки
     * поиска или элемент прошел проверку поиска; если задано
     * свойство service, признак всегда истинен, так как отбор
     * выполняется на стороне сервиса.
     *
     * @param {object|*} item Исходный элемент или его значение.
     * @returns {object} Приведенный элемент списка.
     */
    _clearItem(item) {
      if (item && item._cleared) {
        return item
      }

      const value = get(item, this.$_itemValue, item)
      const title = get(item, this.itemTitle, value)

      const result = {
        [this.itemTitle]: title,
        [this.$_itemValue]: value
      }

      const picks = cloneDeep(this.picks)
      for (const pick of picks) {
        if (pick !== this.itemTitle && pick !== this.$_itemValue) {
          result[pick] = get(item, pick)
        }
      }

      result._item = item
      result._selected = !!~this.proxy.findIndex(v => {
        return this._compareValues(v, value)
      })
      result._filtered = !this.search || this._checkSearchValidity(title, value, item)
      if (this.service) {
        result._filtered = true
      }
      result._disabled = this._getDisabled(item)
      result._cleared = true
      result._icon = get(item, 'icon')
      result._iconSVG = get(item, 'iconSVG')
      result._iconColor = get(item, 'iconColor')
      result._color = get(item, 'color')

      return result
    }
  }
}
